/**
 * `<EnvironmentToolCard />` — INIT-2026-Q3-WEATHER-ENVIRONMENT
 *
 * Per-layer parameter panel for environment layers. Opens in ToolCardDock
 * (Zone D.2) when a child row in the Environment card (Zone B.1) is expanded.
 *
 * ---------------------------------------------------------------------------
 * INTEGRATION POINTS IN SceneTreePanel (for frontend-architect / host wiring)
 * ---------------------------------------------------------------------------
 *
 * 1. SLOT_ORDER / SLOT_LABEL (layerMetadata.ts or equivalent SSOT)
 *    Add `'environment'` after `'overlays'`:
 *      export const SLOT_ORDER = [..., 'overlays', 'environment'] as const;
 *      export const SLOT_LABEL: Record<SceneSlot, string> = {
 *        ..., environment: 'Environment',
 *      };
 *
 * 2. flyToLayer button — MUST be hidden (not just disabled) on all env:* rows.
 *    Guard: `if (layer.style?.isSyntheticEnvironment) return null;` wherever
 *    the fly-to button is rendered in GroupedLayerList / Wave1LayerCard.
 *    Rationale: environment layers are camera-relative with no geographic bbox.
 *
 * 3. Per-row icon — show `ENVIRONMENT_KIND_ICON[layer.style.environmentKind]`
 *    (exported map below) instead of the generic layer-kind icon.
 *
 * 4. Row click → ToolCardDock wiring:
 *    When a child env row label is clicked (or "Open settings" in the ⋯ menu):
 *      dispatchToolCardRequest({
 *        toolCardKind: 'environment',
 *        layerId: layer.id,
 *        environmentKind: layer.style.environmentKind,
 *      });
 *    The ToolCardDock host checks `toolCardKind === 'environment'` and mounts
 *    `<EnvironmentToolCard>` with the layer's current `style.environmentParameters`.
 *
 * 5. Parent card controls (above child rows in the slot header):
 *    a. Master "Use Environment" toggle:
 *         <button role="switch" aria-checked={masterEnabled}
 *           aria-label="Use environment effects" ...>
 *    b. Preset menu: <EnvironmentPresetMenu activePreset={...} onSelectPreset={...}
 *         onReset={...} />
 *    Both belong in the slot group header rendered by GroupedLayerList for the
 *    `'environment'` slot, not inside a child row.
 *
 * 6. env:lightning row (Phase 2):
 *    Render with aria-disabled="true" on toggle, greyed label, "Phase 2" chip.
 *    Parameter panel expand button shows "Open Lightning parameters, button,
 *    unavailable". No EnvironmentToolCard mounted for this kind in V1.
 *
 * 7. env:time-of-day opacity slider: hide (no engine mapping). Replace with a
 *    dash placeholder: `<span aria-hidden>—</span>`.
 *
 * ---------------------------------------------------------------------------
 *
 * Spec references:
 *   - Interaction: interaction-designer brief §2 (per-layer parameter panels)
 *   - Visual: visual-designer brief §4.4 (parameter panel surface treatment)
 *   - Accessibility: accessibility-specialist brief §1.3 (per-layer ARIA)
 *   - Accessibility keyboard: a11y brief §2 (keyboard map)
 *   - Reduced-motion: a11y brief §4 (reduced-motion table)
 *
 * Design-policy compliance:
 *   - No raw hex — all colours via tokens.color.environment.* or other tokens.
 *   - Material Symbols icons (rounded) only, from approved registry.
 *   - 40 px minimum touch targets.
 *   - Full ARIA: role="slider" + aria-valuetext, role="switch" + aria-checked,
 *     role="radiogroup" + role="radio" + aria-checked on all segmented controls.
 *   - Escape key closes panel; focus returns to originating row.
 *   - Reduced motion: CSS transitions suppressed; animation controls noted.
 *   - No floating panels — component renders inside ToolCardDock (Zone D.2).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Icon, tokens, type IconName } from '@/design';
import { WEATHER_MODULE_VERSION } from '../manifest';

// ---------------------------------------------------------------------------
// Types (mirrors environment-taxonomy.md §2–§3, SSOT for Phase 3 engineering)
// ---------------------------------------------------------------------------

export type EnvironmentKind =
  | 'sky'
  | 'sun'
  | 'moon'
  | 'stars'
  | 'fog'
  | 'clouds'
  | 'wind'
  | 'rain'
  | 'snow'
  | 'lightning'
  | 'darkmode'
  | 'time-of-day';

export interface SkyParameters {
  mode: 'clear' | 'overcast' | 'dusk' | 'night' | 'custom';
  intensity: number;
  fogDensity: number;
  fogTint: string;
}

export interface SunParameters {
  hour: number;
  decoupled: boolean;
  intensity: number;
  color: 'warm' | 'neutral' | 'cool';
  castShadows: boolean;
  shadowMapSize: 512 | 1024 | 2048 | 4096;
}

export interface MoonParameters {
  intensity: number;
  phase: number;
  color: 'coldBlue' | 'neutral';
}

export interface StarsParameters {
  density: 'low' | 'medium' | 'high';
  magnitudeLimit: number;
  twinkle: boolean;
  autoSuppress: boolean;
}

export interface FogParameters {
  density: number;
  colour: string;
  near: number;
  far: number;
  mode: 'exp' | 'exp2' | 'linear';
}

export interface CloudsParameters {
  coverage: number;
  altitude: number;
  speed: number;
  direction: number;
  opacity: number;
}

export interface WindParameters {
  speed: number;
  direction: number;
  gust: boolean;
  particleDensity: 'low' | 'medium' | 'high';
  streakLength: number;
  opacity: number;
}

export interface RainParameters {
  intensity: number;
  windCoupled: boolean;
  puddles: boolean;
  dropSize: number;
  fallSpeed: number;
  opacity: number;
}

export interface SnowParameters {
  intensity: number;
  windCoupled: boolean;
  accumulation: number;
  flakeSize: number;
  fallSpeed: number;
  sway: number;
  shape: 'dot' | 'hex' | 'square';
  opacity: number;
}

export interface LightningParameters {
  frequency: number;
  intensity: number;
  coupledWithRain: boolean;
}

export interface DarkmodeParameters {
  enabled: boolean;
  intensity: number;
}

export interface TimeOfDayParameters {
  mode: 'manual' | 'project-clock' | 'system-clock';
  playbackSpeed: number;
  loop: boolean;
  currentHour: number;
}

export type EnvironmentParameters =
  | SkyParameters
  | SunParameters
  | MoonParameters
  | StarsParameters
  | FogParameters
  | CloudsParameters
  | WindParameters
  | RainParameters
  | SnowParameters
  | LightningParameters
  | DarkmodeParameters
  | TimeOfDayParameters;

// ---------------------------------------------------------------------------
// Icon map — used by SceneTreePanel child rows (integration point 3 above)
// ---------------------------------------------------------------------------

export const ENVIRONMENT_KIND_ICON: Record<EnvironmentKind, IconName> = {
  sky: 'wb_twilight' as IconName,
  sun: 'wb_sunny' as IconName,
  moon: 'nights_stay' as IconName,
  stars: 'stars' as IconName,
  fog: 'foggy' as IconName,
  clouds: 'wb_cloudy' as IconName,
  wind: 'air' as IconName,
  rain: 'water_drop' as IconName,
  snow: 'ac_unit' as IconName,
  lightning: 'thunderstorm' as IconName,
  darkmode: 'dark_mode' as IconName,
  'time-of-day': 'av_timer' as IconName,
};

export const ENVIRONMENT_KIND_LABEL: Record<EnvironmentKind, string> = {
  sky: 'Sky & Atmosphere',
  sun: 'Sun & Daylight',
  moon: 'Moon & Night Light',
  stars: 'Stars / Skybox',
  fog: 'Distance Fog',
  clouds: 'Cloud Cover',
  wind: 'Wind',
  rain: 'Rain',
  snow: 'Snow',
  lightning: 'Lightning',
  darkmode: 'Dark Mode / Night Scene',
  'time-of-day': 'Time-of-Day Controller',
};

// ---------------------------------------------------------------------------
// Public component API
// ---------------------------------------------------------------------------

export interface EnvironmentToolCardProps {
  readonly layerId: string;
  readonly environmentKind: EnvironmentKind;
  readonly parameters: Record<string, unknown>;
  readonly onParameterChange: (key: string, value: unknown) => void;
  readonly onClose: () => void;
}

// ---------------------------------------------------------------------------
// Reduced-motion hook
// ---------------------------------------------------------------------------

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return reduced;
}

// ---------------------------------------------------------------------------
// aria-valuetext helper functions (accessibility-specialist brief §1.3)
// ---------------------------------------------------------------------------

/** Convert decimal hours (e.g. 13.5) to "HH:MM" display string. */
export function decimalHoursToHHMM(hour: number): string {
  const h = Math.floor(Math.max(0, Math.min(24, hour)));
  const m = Math.round(((hour % 1) + 1) % 1 * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Koschmieder visibility approximation: vis ≈ −ln(0.02) / density metres. */
export function fogDensityToVisibilityMeters(density: number): number {
  if (density <= 0) return 999_999;
  return Math.round(-Math.log(0.02) / density);
}

/** 8-point compass direction from meteorological degrees (0 = N, 90 = E). */
export function degreesToCompass(degrees: number): string {
  const dirs = [
    'north', 'north-east', 'east', 'south-east',
    'south', 'south-west', 'west', 'north-west',
  ] as const;
  const idx = Math.round(((degrees % 360) + 360) % 360 / 45) % 8;
  return dirs[idx];
}

/** Beaufort scale description for a wind speed in m/s. */
export function windSpeedToBeaufort(speed: number): string {
  if (speed <= 0.5) return 'calm';
  if (speed <= 1.5) return 'light air';
  if (speed <= 3.3) return 'light breeze';
  if (speed <= 5.5) return 'gentle breeze';
  if (speed <= 7.9) return 'moderate breeze';
  if (speed <= 10.7) return 'fresh breeze';
  if (speed <= 13.8) return 'strong breeze';
  if (speed <= 17.1) return 'near gale';
  if (speed <= 20.7) return 'gale';
  if (speed <= 24.4) return 'severe gale';
  if (speed <= 28.4) return 'storm';
  if (speed <= 32.6) return 'violent storm';
  return 'hurricane force';
}

/** Human-readable lunar phase label from 0–1 (0/1 = new, 0.5 = full). */
export function lunarPhaseLabel(phase: number): string {
  const pct = phase * 100;
  if (pct <= 12 || pct >= 88) return 'New moon';
  if (pct <= 37) return 'Crescent';
  if (pct <= 62) return 'Half moon';
  return 'Gibbous moon';
}

/** Context label for rain intensity 0–1. */
export function rainIntensityLabel(intensity: number): string {
  const pct = intensity * 100;
  if (pct <= 20) return 'drizzle';
  if (pct <= 40) return 'light rain';
  if (pct <= 60) return 'moderate rain';
  if (pct <= 80) return 'heavy rain';
  return 'downpour';
}

/** Context label for snow intensity 0–1. */
export function snowIntensityLabel(intensity: number): string {
  const pct = intensity * 100;
  if (pct <= 20) return 'light flurry';
  if (pct <= 50) return 'light snow';
  if (pct <= 75) return 'moderate snow';
  return 'blizzard';
}

/** Cloud coverage okta label from 0–1. */
export function cloudCoverLabel(coverage: number): string {
  const pct = coverage * 100;
  if (pct <= 12) return 'clear sky';
  if (pct <= 37) return 'few clouds';
  if (pct <= 62) return 'scattered';
  if (pct <= 87) return 'broken cloud cover';
  return 'overcast';
}

// ---------------------------------------------------------------------------
// Reduced-motion aware transition helper
// ---------------------------------------------------------------------------

function transitionValue(property: string, durationMs: number, easing: string): string {
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return 'none';
  }
  return `${property} ${durationMs}ms ${easing}`;
}

// ---------------------------------------------------------------------------
// Shared primitive: SegmentedControl (radiogroup)
// ---------------------------------------------------------------------------

interface SegmentOption {
  value: string;
  label: string;
  ariaLabel?: string;
}

interface SegmentedControlProps {
  groupLabel: string;
  options: readonly SegmentOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

function SegmentedControl({
  groupLabel,
  options,
  value,
  onChange,
  disabled = false,
}: SegmentedControlProps) {
  const groupRef = useRef<HTMLDivElement | null>(null);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      const { key } = event;
      if (key !== 'ArrowRight' && key !== 'ArrowDown' && key !== 'ArrowLeft' && key !== 'ArrowUp') return;

      event.preventDefault();
      const currentIdx = options.findIndex((o) => o.value === value);
      let nextIdx: number;
      if (key === 'ArrowRight' || key === 'ArrowDown') {
        nextIdx = (currentIdx + 1) % options.length;
      } else {
        nextIdx = (currentIdx - 1 + options.length) % options.length;
      }
      onChange(options[nextIdx].value);

      const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
      buttons?.[nextIdx]?.focus();
    },
    [disabled, options, value, onChange],
  );

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={groupLabel}
      onKeyDown={handleKeyDown}
      style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}
    >
      {options.map((opt, idx) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.ariaLabel ?? opt.label}
            disabled={disabled}
            tabIndex={active ? 0 : -1}
            onClick={() => !disabled && onChange(opt.value)}
            style={segmentButtonStyle(active, idx, options.length, disabled)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function segmentButtonStyle(
  active: boolean,
  idx: number,
  total: number,
  disabled: boolean,
): CSSProperties {
  return {
    flex: 1,
    minHeight: tokens.density.touchMin,
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderLeft: idx > 0 ? 0 : tokens.border.thin,
    borderRadius:
      idx === 0
        ? `${tokens.radius.input}px 0 0 ${tokens.radius.input}px`
        : idx === total - 1
          ? `0 ${tokens.radius.input}px ${tokens.radius.input}px 0`
          : 0,
    background: active ? tokens.color.accent.subtle : tokens.color.surface[3],
    color: active ? tokens.color.accent.primary : tokens.color.ink.secondary,
    fontSize: tokens.font.role.label.size,
    fontWeight: active ? tokens.font.weight.medium : tokens.font.weight.regular,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    transition: transitionValue('background', tokens.motion.duration.fast, tokens.motion.easing.decel),
    whiteSpace: 'nowrap',
  };
}

// ---------------------------------------------------------------------------
// Shared primitive: ParamSwitch (role="switch")
// ---------------------------------------------------------------------------

interface ParamSwitchProps {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  disabledReason?: string;
  hint?: string;
}

function ParamSwitch({
  label,
  checked,
  onChange,
  disabled = false,
  disabledReason,
  hint,
}: ParamSwitchProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.space[2],
        minHeight: tokens.density.touchMin,
      }}
    >
      <div style={{ flex: 1 }}>
        <span
          style={{
            fontSize: tokens.font.role.body.size,
            color: disabled ? tokens.color.ink.muted : tokens.color.ink.primary,
          }}
        >
          {label}
        </span>
        {hint ? (
          <p
            style={{
              margin: 0,
              marginTop: tokens.space[1],
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
            }}
          >
            {hint}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onClick={() => !disabled && onChange(!checked)}
        style={switchButtonStyle(checked, disabled)}
      >
        <span
          aria-hidden
          style={switchThumbStyle(checked)}
        />
        <span
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
          }}
        >
          {checked ? 'on' : 'off'}
        </span>
      </button>
    </div>
  );
}

function switchButtonStyle(checked: boolean, disabled: boolean): CSSProperties {
  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    width: tokens.size.switchMinWidth + 12,
    height: tokens.size.switchMinHeight + 8,
    minWidth: tokens.density.touchMin,
    minHeight: tokens.density.touchMin,
    border: 0,
    borderRadius: tokens.radius.pill,
    background: checked ? tokens.color.accent.primary : tokens.color.surface[3],
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    padding: `0 ${tokens.space[1]}px`,
    transition: transitionValue('background', tokens.motion.duration.fast, tokens.motion.easing.standard),
    flexShrink: 0,
  };
}

function switchThumbStyle(checked: boolean): CSSProperties {
  return {
    display: 'block',
    width: tokens.size.switchMinHeight,
    height: tokens.size.switchMinHeight,
    borderRadius: tokens.radius.pill,
    background: tokens.color.surface[0],
    transform: checked ? `translateX(${tokens.size.switchMinWidth - 8}px)` : 'translateX(0)',
    transition: transitionValue('transform', tokens.motion.duration.fast, tokens.motion.easing.standard),
    flexShrink: 0,
  };
}

// ---------------------------------------------------------------------------
// Shared primitive: ParamSlider
// ---------------------------------------------------------------------------

interface ParamSliderProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  valueText: string;
  displayValue: string;
  disabled?: boolean;
  disabledReason?: string;
}

function ParamSlider({
  label,
  min,
  max,
  step,
  value,
  onChange,
  valueText,
  displayValue,
  disabled = false,
  disabledReason,
}: ParamSliderProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <label
          style={{
            fontSize: tokens.font.role.body.size,
            color: disabled ? tokens.color.ink.muted : tokens.color.ink.primary,
          }}
        >
          {label}
        </label>
        <span
          style={{
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
            color: disabled ? tokens.color.ink.muted : tokens.color.ink.secondary,
            minWidth: tokens.space[7],
            textAlign: 'right',
          }}
        >
          {displayValue}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onChange={(e) => !disabled && onChange(Number(e.currentTarget.value))}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={valueText}
        aria-disabled={disabled || undefined}
        style={{
          width: '100%',
          minHeight: tokens.density.touchMin,
          accentColor: tokens.color.accent.primary,
          opacity: disabled ? 0.4 : 1,
          cursor: disabled ? 'not-allowed' : undefined,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared primitive: SectionHeader
// ---------------------------------------------------------------------------

function SectionHeader({ children }: { children: string }) {
  return (
    <p
      style={{
        margin: 0,
        marginBottom: tokens.space[2],
        fontSize: tokens.font.role.engMeta.size,
        fontWeight: tokens.font.weight.medium,
        color: tokens.color.ink.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Shared primitive: SectionDivider
// ---------------------------------------------------------------------------

function SectionDivider() {
  return (
    <div
      role="separator"
      style={{
        height: 0,
        borderTop: tokens.border.thin,
        marginBlock: tokens.space[3],
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Phase-2 disabled notice
// ---------------------------------------------------------------------------

function Phase2Notice({ label }: { label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        padding: `${tokens.space[3]}px`,
        borderRadius: tokens.radius.md,
        background: tokens.color.surface[3],
      }}
    >
      <Icon
        name="schedule"
        size={20}
        decorative
        style={{ color: tokens.color.ink.muted, flexShrink: 0 }}
      />
      <span
        style={{
          fontSize: tokens.font.role.body.size,
          color: tokens.color.ink.muted,
        }}
      >
        {label} controls will be available in a future update. Lightning fires
        automatically in the Storm preset.
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-kind parameter panels
// ---------------------------------------------------------------------------

function SkyPanel({
  params,
  onChange,
  reducedMotion,
}: {
  params: SkyParameters;
  onChange: (key: string, value: unknown) => void;
  reducedMotion: boolean;
}) {
  const intensityPct = Math.round(params.intensity * 100);
  const densityPct = Math.round(
    params.fogDensity > 0
      ? (Math.log10(params.fogDensity / 0.00001) / Math.log10(0.001 / 0.00001)) * 100
      : 0,
  );

  return (
    <div>
      <SectionHeader>Sky Mode</SectionHeader>
      <SegmentedControl
        groupLabel="Sky mode"
        options={[
          { value: 'clear', label: 'Clear', ariaLabel: 'Clear sky' },
          { value: 'overcast', label: 'Overcast', ariaLabel: 'Overcast sky' },
          { value: 'dusk', label: 'Dusk', ariaLabel: 'Dusk sky' },
          { value: 'night', label: 'Night', ariaLabel: 'Night sky' },
          { value: 'custom', label: 'Custom', ariaLabel: 'Custom sky' },
        ]}
        value={params.mode}
        onChange={(v) => onChange('mode', v)}
      />

      <SectionDivider />

      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
        <ParamSlider
          label="Sky intensity"
          min={0}
          max={100}
          step={1}
          value={intensityPct}
          onChange={(v) => onChange('intensity', v / 100)}
          valueText={`Sky intensity ${intensityPct} percent`}
          displayValue={`${intensityPct}%`}
        />

        <ParamSlider
          label="Sky Fog Density"
          min={0}
          max={100}
          step={1}
          value={densityPct}
          onChange={(v) => {
            const density = 0.00001 * Math.pow(100, v / 100);
            onChange('fogDensity', Math.min(0.001, density));
          }}
          valueText={`Sky fog density ${params.fogDensity.toFixed(5)}, visibility approximately ${fogDensityToVisibilityMeters(params.fogDensity).toLocaleString()} meters`}
          displayValue={params.fogDensity.toFixed(5)}
        />

        <div>
          <label
            style={{
              fontSize: tokens.font.role.body.size,
              color: tokens.color.ink.primary,
              display: 'block',
              marginBottom: tokens.space[1],
            }}
          >
            Fog Tint Colour
          </label>
          <button
            type="button"
            role="button"
            aria-label={`Sky fog tint colour, current value ${params.fogTint}`}
            aria-haspopup="dialog"
            style={colorSwatchButtonStyle(params.fogTint)}
          >
            <span
              aria-hidden
              style={{
                display: 'block',
                width: 24,
                height: 24,
                borderRadius: tokens.radius.sm,
                background: params.fogTint,
              }}
            />
            <span
              style={{
                fontFamily: tokens.font.family.mono,
                fontSize: tokens.font.role.engMono.size,
                color: tokens.color.ink.secondary,
              }}
            >
              {params.fogTint}
            </span>
          </button>
          <p
            style={{
              margin: `${tokens.space[1]}px 0 0`,
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
            }}
          >
            Use tokens.color.environment.fog.* values for preset tints.
          </p>
        </div>
      </div>

      {!reducedMotion ? null : (
        <p
          style={{
            marginTop: tokens.space[3],
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          Animated sky transitions are paused (Reduce Motion is on).
        </p>
      )}
    </div>
  );
}

function SunPanel({
  params,
  onChange,
  reducedMotion,
}: {
  params: SunParameters;
  onChange: (key: string, value: unknown) => void;
  reducedMotion: boolean;
}) {
  const hourText = decimalHoursToHHMM(params.hour);
  const intensityDisplay = params.intensity.toFixed(1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <ParamSlider
        label="Sun hour of day"
        min={0}
        max={24}
        step={0.25}
        value={params.hour}
        onChange={(v) => onChange('hour', v)}
        valueText={`Sun position ${hourText}`}
        displayValue={hourText}
      />

      <ParamSwitch
        label="Decouple sun from project time"
        checked={params.decoupled}
        onChange={(v) => onChange('decoupled', v)}
        hint="When on, sun position ignores the Time-of-Day controller"
      />

      <SectionDivider />

      <ParamSlider
        label="Sun light intensity"
        min={0}
        max={300}
        step={10}
        value={Math.round(params.intensity * 100)}
        onChange={(v) => onChange('intensity', v / 100)}
        valueText={`Sun intensity ${intensityDisplay}, range 0 to 3`}
        displayValue={intensityDisplay}
      />

      <div>
        <SectionHeader>Colour Temperature</SectionHeader>
        <SegmentedControl
          groupLabel="Sun colour temperature"
          options={[
            { value: 'warm', label: 'Warm', ariaLabel: 'Warm (sunrise/sunset tones)' },
            { value: 'neutral', label: 'Neutral', ariaLabel: 'Neutral (midday white light)' },
            { value: 'cool', label: 'Cool', ariaLabel: 'Cool (overcast tones)' },
          ]}
          value={params.color}
          onChange={(v) => onChange('color', v)}
        />
      </div>

      <SectionDivider />

      <ParamSwitch
        label="Enable sun shadow casting"
        checked={params.castShadows}
        onChange={(v) => onChange('castShadows', v)}
        hint={params.castShadows ? 'Shadows enabled · higher GPU load' : undefined}
      />

      {params.castShadows ? (
        <div>
          <SectionHeader>Shadow Map Resolution</SectionHeader>
          <SegmentedControl
            groupLabel="Shadow map resolution"
            options={[
              { value: '512', label: '512', ariaLabel: '512 pixels, low quality' },
              { value: '1024', label: '1 K', ariaLabel: '1024 pixels' },
              { value: '2048', label: '2 K', ariaLabel: '2048 pixels (default)' },
              { value: '4096', label: '4 K', ariaLabel: '4096 pixels, higher GPU cost' },
            ]}
            value={String(params.shadowMapSize)}
            onChange={(v) => onChange('shadowMapSize', Number(v) as 512 | 1024 | 2048 | 4096)}
          />
        </div>
      ) : null}

      {!reducedMotion ? null : (
        <p
          style={{
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          Real-time sun updates continue (user-initiated interactions are
          exempt from Reduce Motion).
        </p>
      )}
    </div>
  );
}

function MoonPanel({
  params,
  onChange,
}: {
  params: MoonParameters;
  onChange: (key: string, value: unknown) => void;
}) {
  const intensityPct = Math.round(params.intensity * 100);
  const phasePct = Math.round(params.phase * 100);
  const phaseText = lunarPhaseLabel(params.phase);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <ParamSlider
        label="Moon light intensity"
        min={0}
        max={100}
        step={5}
        value={intensityPct}
        onChange={(v) => onChange('intensity', v / 100)}
        valueText={`Moonlight intensity ${intensityPct} percent`}
        displayValue={`${intensityPct}%`}
      />

      <div>
        <ParamSlider
          label="Lunar Phase"
          min={0}
          max={100}
          step={5}
          value={phasePct}
          onChange={(v) => onChange('phase', v / 100)}
          valueText={phaseText}
          displayValue={phaseText}
        />
        <div
          aria-hidden
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: tokens.space[1],
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          <span>New</span>
          <span>Full</span>
          <span>New</span>
        </div>
      </div>

      <SectionDivider />

      <div>
        <SectionHeader>Colour</SectionHeader>
        <SegmentedControl
          groupLabel="Moonlight colour"
          options={[
            { value: 'coldBlue', label: 'Cold Blue', ariaLabel: 'Cold blue (clear night)' },
            { value: 'neutral', label: 'Neutral', ariaLabel: 'Neutral white' },
          ]}
          value={params.color}
          onChange={(v) => onChange('color', v)}
        />
      </div>
    </div>
  );
}

function StarsPanel({
  params,
  onChange,
  reducedMotion,
}: {
  params: StarsParameters;
  onChange: (key: string, value: unknown) => void;
  reducedMotion: boolean;
}) {
  const magDisplay = params.magnitudeLimit.toFixed(1);
  const magStars =
    params.magnitudeLimit <= 3.5
      ? 'about 170 stars'
      : params.magnitudeLimit <= 5.5
        ? 'about 1,500 stars'
        : 'about 9,000 stars';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <div>
        <SectionHeader>Star Density</SectionHeader>
        <SegmentedControl
          groupLabel="Star field density"
          options={[
            { value: 'low', label: 'Low', ariaLabel: 'Low density, bright stars only' },
            { value: 'medium', label: 'Medium', ariaLabel: 'Medium density' },
            { value: 'high', label: 'High', ariaLabel: 'High density, faint stars visible' },
          ]}
          value={params.density}
          onChange={(v) => onChange('density', v)}
        />
      </div>

      <ParamSlider
        label="Star magnitude limit"
        min={30}
        max={65}
        step={5}
        value={Math.round(params.magnitudeLimit * 10)}
        onChange={(v) => onChange('magnitudeLimit', v / 10)}
        valueText={`Limiting magnitude ${magDisplay}, ${magStars} visible to naked eye`}
        displayValue={magDisplay}
      />

      <SectionDivider />

      <ParamSwitch
        label="Enable star twinkle animation"
        checked={reducedMotion ? false : params.twinkle}
        onChange={(v) => onChange('twinkle', v)}
        disabled={reducedMotion}
        disabledReason="Disabled by Reduce Motion system preference"
        hint={
          reducedMotion
            ? 'Disabled by Reduce Motion system preference'
            : 'Sinusoidal opacity variation per star'
        }
      />

      <ParamSwitch
        label="Auto-hide stars above civil twilight"
        checked={params.autoSuppress}
        onChange={(v) => onChange('autoSuppress', v)}
        hint="Stars fade out when sun elevation > −6°"
      />
    </div>
  );
}

function FogPanel({
  params,
  onChange,
}: {
  params: FogParameters;
  onChange: (key: string, value: unknown) => void;
}) {
  const visMet = fogDensityToVisibilityMeters(params.density);
  const densityPct =
    params.density > 0
      ? Math.round(
          (Math.log10(params.density / 0.00001) / Math.log10(0.001 / 0.00001)) * 100,
        )
      : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <div>
        <SectionHeader>Fog Mode</SectionHeader>
        <SegmentedControl
          groupLabel="Fog attenuation mode"
          options={[
            { value: 'exp', label: 'Exp', ariaLabel: 'Exponential fog' },
            { value: 'exp2', label: 'Exp²', ariaLabel: 'Exponential squared fog (default)' },
            { value: 'linear', label: 'Linear', ariaLabel: 'Linear fog (near and far planes active)' },
          ]}
          value={params.mode}
          onChange={(v) => onChange('mode', v)}
        />
      </div>

      {params.mode !== 'linear' ? (
        <ParamSlider
          label="Fog density"
          min={0}
          max={100}
          step={1}
          value={densityPct}
          onChange={(v) => {
            const d = 0.00001 * Math.pow(100, v / 100);
            onChange('density', Math.min(0.001, d));
          }}
          valueText={`Fog density ${params.density.toFixed(5)}, visibility approximately ${visMet.toLocaleString()} meters`}
          displayValue={params.density.toFixed(5)}
        />
      ) : null}

      {params.mode === 'linear' ? (
        <>
          <ParamSlider
            label="Near Plane"
            min={0}
            max={5000}
            step={50}
            value={params.near}
            onChange={(v) => onChange('near', v)}
            valueText={`Fog near plane ${params.near} meters`}
            displayValue={`${params.near} m`}
          />
          <ParamSlider
            label="Far Plane"
            min={100}
            max={50000}
            step={100}
            value={params.far}
            onChange={(v) => onChange('far', v)}
            valueText={`Fog far plane ${params.far} meters`}
            displayValue={`${params.far} m`}
          />
        </>
      ) : null}

      <SectionDivider />

      <div>
        <label
          style={{
            fontSize: tokens.font.role.body.size,
            color: tokens.color.ink.primary,
            display: 'block',
            marginBottom: tokens.space[1],
          }}
        >
          Fog Colour
        </label>
        <button
          type="button"
          role="button"
          aria-label={`Fog colour, current value ${params.colour}`}
          aria-haspopup="dialog"
          style={colorSwatchButtonStyle(params.colour)}
        >
          <span
            aria-hidden
            style={{
              display: 'block',
              width: 24,
              height: 24,
              borderRadius: tokens.radius.sm,
              background: params.colour,
            }}
          />
          <span
            style={{
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
              color: tokens.color.ink.secondary,
            }}
          >
            {params.colour}
          </span>
        </button>
      </div>
    </div>
  );
}

function CloudsPanel({
  params,
  onChange,
}: {
  params: CloudsParameters;
  onChange: (key: string, value: unknown) => void;
}) {
  const coveragePct = Math.round(params.coverage * 100);
  const coverageLabel = cloudCoverLabel(params.coverage);
  const compassDir = degreesToCompass(params.direction);
  const speedDisplay = params.speed.toFixed(1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <div>
        <ParamSlider
          label="Cloud coverage"
          min={0}
          max={100}
          step={5}
          value={coveragePct}
          onChange={(v) => onChange('coverage', v / 100)}
          valueText={`Cloud coverage ${coveragePct} percent, ${coverageLabel}`}
          displayValue={`${coveragePct}%`}
        />
        <div
          aria-hidden
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: tokens.space[1],
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          <span>Clear</span>
          <span>{coverageLabel}</span>
          <span>Overcast</span>
        </div>
      </div>

      <div>
        <label
          style={{
            fontSize: tokens.font.role.body.size,
            color: tokens.color.ink.primary,
            display: 'block',
            marginBottom: tokens.space[1],
          }}
        >
          Cloud Base Altitude
        </label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            height: tokens.density.touchMin,
            paddingInline: tokens.space[2],
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            background: tokens.color.surface[3],
          }}
        >
          <input
            type="number"
            min={200}
            max={13000}
            step={100}
            value={params.altitude}
            onChange={(e) => onChange('altitude', Number(e.currentTarget.value))}
            aria-label="Cloud base altitude above ground level"
            aria-valuemin={200}
            aria-valuemax={13000}
            aria-valuenow={params.altitude}
            aria-valuetext={`Cloud altitude ${params.altitude} meters above ground`}
            style={{
              flex: 1,
              border: 0,
              outline: 0,
              background: 'transparent',
              color: tokens.color.ink.primary,
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
            }}
          />
          <span
            style={{
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
            }}
          >
            m AGL
          </span>
        </div>
      </div>

      <ParamSlider
        label="Cloud animation speed"
        min={0}
        max={100}
        step={5}
        value={Math.round(params.speed * 10)}
        onChange={(v) => onChange('speed', v / 10)}
        valueText={`Cloud animation speed ${Math.round(params.speed * 10)} percent of maximum`}
        displayValue={speedDisplay}
      />

      <div>
        <label
          style={{
            fontSize: tokens.font.role.body.size,
            color: tokens.color.ink.primary,
            display: 'block',
            marginBottom: tokens.space[1],
          }}
        >
          Wind Direction
        </label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            height: tokens.density.touchMin,
            paddingInline: tokens.space[2],
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            background: tokens.color.surface[3],
          }}
        >
          <input
            type="number"
            min={0}
            max={360}
            step={5}
            value={params.direction}
            onChange={(e) => onChange('direction', Number(e.currentTarget.value))}
            aria-label="Cloud movement direction in degrees from north"
            aria-valuemin={0}
            aria-valuemax={360}
            aria-valuenow={params.direction}
            aria-valuetext={`Cloud movement direction ${params.direction} degrees, ${compassDir}`}
            style={{
              flex: 1,
              border: 0,
              outline: 0,
              background: 'transparent',
              color: tokens.color.ink.primary,
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
            }}
          />
          <span
            style={{
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
              minWidth: tokens.space[7],
              textAlign: 'right',
            }}
          >
            ° from N · {compassDir.toUpperCase()}
          </span>
        </div>
      </div>
    </div>
  );
}

function WindPanel({
  params,
  onChange,
}: {
  params: WindParameters;
  onChange: (key: string, value: unknown) => void;
}) {
  const beaufort = windSpeedToBeaufort(params.speed);
  const compass = degreesToCompass(params.direction);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <div>
        <ParamSlider
          label="Wind Speed"
          min={0}
          max={40}
          step={0.5}
          value={params.speed}
          onChange={(v) => onChange('speed', v)}
          valueText={`Wind speed ${params.speed} meters per second, ${beaufort}`}
          displayValue={`${params.speed} m/s`}
        />
        <p
          aria-hidden
          style={{
            margin: `${tokens.space[1]}px 0 0`,
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          {beaufort.charAt(0).toUpperCase() + beaufort.slice(1)}
        </p>
      </div>

      <div>
        <label
          style={{
            fontSize: tokens.font.role.body.size,
            color: tokens.color.ink.primary,
            display: 'block',
            marginBottom: tokens.space[1],
          }}
        >
          Wind Direction
        </label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            height: tokens.density.touchMin,
            paddingInline: tokens.space[2],
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            background: tokens.color.surface[3],
          }}
        >
          <input
            type="number"
            min={0}
            max={360}
            step={5}
            value={params.direction}
            onChange={(e) => onChange('direction', Number(e.currentTarget.value))}
            aria-label="Wind direction in degrees from north"
            aria-valuemin={0}
            aria-valuemax={360}
            aria-valuenow={params.direction}
            aria-valuetext={`Wind direction ${params.direction} degrees, ${compass}`}
            style={{
              flex: 1,
              border: 0,
              outline: 0,
              background: 'transparent',
              color: tokens.color.ink.primary,
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
            }}
          />
          <span
            style={{
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
              minWidth: tokens.space[7],
              textAlign: 'right',
            }}
          >
            ° from N · {compass.slice(0, 2).toUpperCase()}
          </span>
        </div>
      </div>

      <SectionDivider />

      <ParamSwitch
        label="Enable wind gusts, random 20 percent speed variation"
        checked={params.gust}
        onChange={(v) => onChange('gust', v)}
        hint="Random ±20% speed variation"
      />

      <div>
        <SectionHeader>Streamline Density</SectionHeader>
        <SegmentedControl
          groupLabel="Wind particle density"
          options={[
            { value: 'low', label: 'Low', ariaLabel: 'Low particle density' },
            { value: 'medium', label: 'Medium', ariaLabel: 'Medium particle density' },
            { value: 'high', label: 'High', ariaLabel: 'High particle density' },
          ]}
          value={params.particleDensity}
          onChange={(v) => onChange('particleDensity', v)}
        />
      </div>

      <SectionDivider />

      <SectionHeader>Appearance</SectionHeader>
      <ParamSlider
        label="Streak length"
        min={0.3}
        max={3}
        step={0.1}
        value={params.streakLength ?? 1}
        onChange={(v) => onChange('streakLength', v)}
        valueText={`Wind streak length ${(params.streakLength ?? 1).toFixed(1)} times`}
        displayValue={`${(params.streakLength ?? 1).toFixed(1)}×`}
      />
      <ParamSlider
        label="Opacity"
        min={0}
        max={100}
        step={5}
        value={Math.round((params.opacity ?? 0.5) * 100)}
        onChange={(v) => onChange('opacity', v / 100)}
        valueText={`Wind opacity ${Math.round((params.opacity ?? 0.5) * 100)} percent`}
        displayValue={`${Math.round((params.opacity ?? 0.5) * 100)}%`}
      />
    </div>
  );
}

function RainPanel({
  params,
  onChange,
  windLayerActive,
}: {
  params: RainParameters;
  onChange: (key: string, value: unknown) => void;
  windLayerActive?: boolean;
}) {
  const intensityPct = Math.round(params.intensity * 100);
  const intensityLabel = rainIntensityLabel(params.intensity);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <div>
        <ParamSlider
          label="Rain intensity"
          min={0}
          max={100}
          step={5}
          value={intensityPct}
          onChange={(v) => onChange('intensity', v / 100)}
          valueText={`Rain intensity ${intensityPct} percent, ${intensityLabel}`}
          displayValue={`${intensityPct}%`}
        />
        <div
          aria-hidden
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: tokens.space[1],
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          <span>Drizzle</span>
          <span>{intensityLabel}</span>
          <span>Downpour</span>
        </div>
      </div>

      <ParamSwitch
        label="Couple rain to wind direction and speed"
        checked={params.windCoupled}
        onChange={(v) => onChange('windCoupled', v)}
        disabled={windLayerActive === false}
        disabledReason="Enable Wind layer to use this setting"
        hint={
          windLayerActive === false
            ? 'Enable Wind layer to use wind coupling'
            : 'Fall angle follows Wind layer direction and speed'
        }
      />

      <SectionDivider />

      <SectionHeader>Appearance</SectionHeader>
      <ParamSlider
        label="Drop size"
        min={0.3}
        max={3}
        step={0.1}
        value={params.dropSize ?? 1}
        onChange={(v) => onChange('dropSize', v)}
        valueText={`Rain drop size ${(params.dropSize ?? 1).toFixed(1)} times`}
        displayValue={`${(params.dropSize ?? 1).toFixed(1)}×`}
      />
      <ParamSlider
        label="Fall speed"
        min={0.3}
        max={3}
        step={0.1}
        value={params.fallSpeed ?? 1}
        onChange={(v) => onChange('fallSpeed', v)}
        valueText={`Rain fall speed ${(params.fallSpeed ?? 1).toFixed(1)} times`}
        displayValue={`${(params.fallSpeed ?? 1).toFixed(1)}×`}
      />
      <ParamSlider
        label="Opacity"
        min={0}
        max={100}
        step={5}
        value={Math.round((params.opacity ?? 0.5) * 100)}
        onChange={(v) => onChange('opacity', v / 100)}
        valueText={`Rain opacity ${Math.round((params.opacity ?? 0.5) * 100)} percent`}
        displayValue={`${Math.round((params.opacity ?? 0.5) * 100)}%`}
      />

      <SectionDivider />

      <ParamSwitch
        label="Enable ground puddles, not available in this version"
        checked={params.puddles}
        onChange={() => undefined}
        disabled
        disabledReason="Coming in a future update"
        hint="Ground puddle decals — coming in a future update"
      />
    </div>
  );
}

function SnowPanel({
  params,
  onChange,
  windLayerActive,
}: {
  params: SnowParameters;
  onChange: (key: string, value: unknown) => void;
  windLayerActive?: boolean;
}) {
  const intensityPct = Math.round(params.intensity * 100);
  const intensityLabel = snowIntensityLabel(params.intensity);
  const accPct = Math.round(params.accumulation * 100);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <div>
        <ParamSlider
          label="Snow intensity"
          min={0}
          max={100}
          step={5}
          value={intensityPct}
          onChange={(v) => onChange('intensity', v / 100)}
          valueText={`Snow intensity ${intensityPct} percent, ${intensityLabel}`}
          displayValue={`${intensityPct}%`}
        />
        <div
          aria-hidden
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: tokens.space[1],
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          <span>Flurry</span>
          <span>{intensityLabel}</span>
          <span>Blizzard</span>
        </div>
      </div>

      <ParamSwitch
        label="Couple snow drift to wind direction and speed"
        checked={params.windCoupled}
        onChange={(v) => onChange('windCoupled', v)}
        disabled={windLayerActive === false}
        disabledReason="Enable Wind layer to use this setting"
        hint={
          windLayerActive === false
            ? 'Enable Wind layer to use wind coupling'
            : 'Drift follows Wind layer direction and speed'
        }
      />

      <SectionDivider />

      <SectionHeader>Appearance</SectionHeader>
      <div>
        <SectionHeader>Flake shape</SectionHeader>
        <SegmentedControl
          groupLabel="Snowflake shape"
          options={[
            { value: 'dot', label: 'Dot', ariaLabel: 'Round flakes' },
            { value: 'hex', label: 'Hex', ariaLabel: 'Hexagonal flakes' },
            { value: 'square', label: 'Square', ariaLabel: 'Square flakes' },
          ]}
          value={params.shape ?? 'dot'}
          onChange={(v) => onChange('shape', v)}
        />
      </div>
      <ParamSlider
        label="Flake size"
        min={0.3}
        max={3}
        step={0.1}
        value={params.flakeSize ?? 1}
        onChange={(v) => onChange('flakeSize', v)}
        valueText={`Snow flake size ${(params.flakeSize ?? 1).toFixed(1)} times`}
        displayValue={`${(params.flakeSize ?? 1).toFixed(1)}×`}
      />
      <ParamSlider
        label="Fall speed"
        min={0.3}
        max={3}
        step={0.1}
        value={params.fallSpeed ?? 1}
        onChange={(v) => onChange('fallSpeed', v)}
        valueText={`Snow fall speed ${(params.fallSpeed ?? 1).toFixed(1)} times`}
        displayValue={`${(params.fallSpeed ?? 1).toFixed(1)}×`}
      />
      <ParamSlider
        label="Sway"
        min={0}
        max={3}
        step={0.1}
        value={params.sway ?? 1}
        onChange={(v) => onChange('sway', v)}
        valueText={`Snow sway ${(params.sway ?? 1).toFixed(1)} times`}
        displayValue={`${(params.sway ?? 1).toFixed(1)}×`}
      />
      <ParamSlider
        label="Opacity"
        min={0}
        max={100}
        step={5}
        value={Math.round((params.opacity ?? 0.85) * 100)}
        onChange={(v) => onChange('opacity', v / 100)}
        valueText={`Snow opacity ${Math.round((params.opacity ?? 0.85) * 100)} percent`}
        displayValue={`${Math.round((params.opacity ?? 0.85) * 100)}%`}
      />

      <SectionDivider />

      <ParamSlider
        label="Accumulation (Phase 2)"
        min={0}
        max={100}
        step={5}
        value={accPct}
        onChange={() => undefined}
        valueText="Snow accumulation depth, not available in this version"
        displayValue={`${accPct}%`}
        disabled
        disabledReason="Coming in a future update"
      />
    </div>
  );
}

function LightningPanel() {
  return <Phase2Notice label="Lightning" />;
}

function DarkmodePanel({
  params,
  onChange,
}: {
  params: DarkmodeParameters;
  onChange: (key: string, value: unknown) => void;
}) {
  const intensityPct = Math.round(params.intensity * 100);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <ParamSlider
        label="Night scene intensity"
        min={0}
        max={100}
        step={5}
        value={intensityPct}
        onChange={(v) => onChange('intensity', v / 100)}
        valueText={`Night scene darkness ${intensityPct} percent`}
        displayValue={`${intensityPct}%`}
      />

      <SectionDivider />

      <div
        role="group"
        aria-label="Layers controlled by Dark Mode"
        style={{
          padding: tokens.space[3],
          borderRadius: tokens.radius.md,
          background: tokens.color.surface[3],
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[1],
        }}
      >
        <p
          style={{
            margin: 0,
            marginBottom: tokens.space[2],
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
            fontWeight: tokens.font.weight.medium,
            textTransform: 'uppercase',
          }}
        >
          Layers controlled by Dark Mode
        </p>
        {[
          'Sun: OFF when Dark Mode is ON',
          'Moon: ON when Dark Mode is ON',
          'Stars: ON when Dark Mode is ON',
          'Fog: tinted to night colour',
          'Sky: switches to night mode',
        ].map((line) => (
          <p
            key={line}
            style={{
              margin: 0,
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.secondary,
            }}
          >
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

function TimeOfDayPanel({
  params,
  onChange,
  reducedMotion,
}: {
  params: TimeOfDayParameters;
  onChange: (key: string, value: unknown) => void;
  reducedMotion: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const currentHourText = decimalHoursToHHMM(params.currentHour);

  // Playback: advance currentHour by `playbackSpeed`× real time while playing.
  // Each tick round-trips through onChange so the slider, the host params, and
  // the engine (sun + sky) all stay in sync.
  const hourRef = useRef(params.currentHour);
  useEffect(() => {
    hourRef.current = params.currentHour;
  }, [params.currentHour]);

  useEffect(() => {
    if (!playing || params.mode !== 'manual') return;

    const advance = (dtSec: number) => {
      let h = hourRef.current + (params.playbackSpeed * dtSec) / 3600;
      if (h >= 24) {
        if (params.loop) h -= 24;
        else h = 24;
      }
      hourRef.current = h;
      onChange('currentHour', Math.round(h * 100) / 100);
      if (h >= 24 && !params.loop) setPlaying(false);
    };

    if (reducedMotion) {
      const handle = setInterval(() => advance(1), 1000);
      return () => clearInterval(handle);
    }

    let raf = 0;
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      advance(dt);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, params.mode, params.playbackSpeed, params.loop, reducedMotion, onChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <div>
        <SectionHeader>Clock Mode</SectionHeader>
        <SegmentedControl
          groupLabel="Time-of-day clock mode"
          options={[
            { value: 'manual', label: 'Manual', ariaLabel: 'Manual, set time with slider' },
            {
              value: 'project-clock',
              label: 'Project',
              ariaLabel: 'Project clock, driven by 4D timeline',
            },
            {
              value: 'system-clock',
              label: 'System',
              ariaLabel: 'System clock, uses current real time',
            },
          ]}
          value={params.mode}
          onChange={(v) => onChange('mode', v)}
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: tokens.density.touchMin,
          paddingInline: tokens.space[2],
          border: tokens.border.thin,
          borderRadius: tokens.radius.input,
          background: tokens.color.surface[3],
        }}
      >
        <span
          style={{
            fontSize: tokens.font.role.body.size,
            color: tokens.color.ink.muted,
          }}
        >
          Current Time
        </span>
        <span
          aria-label={`Current simulation time ${currentHourText}`}
          aria-live={params.mode === 'manual' ? undefined : 'polite'}
          style={{
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
            color: tokens.color.ink.primary,
          }}
        >
          {currentHourText}
        </span>
      </div>

      {params.mode === 'manual' ? (
        <>
          <ParamSlider
            label="Current time of day"
            min={0}
            max={24}
            step={0.25}
            value={params.currentHour}
            onChange={(v) => onChange('currentHour', v)}
            valueText={`Time of day ${decimalHoursToHHMM(params.currentHour)}`}
            displayValue={decimalHoursToHHMM(params.currentHour)}
          />

          <ParamSlider
            label="Time-of-day playback speed"
            min={1}
            max={1000}
            step={1}
            value={params.playbackSpeed}
            onChange={(v) => onChange('playbackSpeed', v)}
            valueText={`Playback speed ${params.playbackSpeed} times real time`}
            displayValue={`${params.playbackSpeed}×`}
          />

          {params.playbackSpeed > 1 ? (
            <div
              style={{
                display: 'flex',
                gap: tokens.space[2],
                alignItems: 'center',
              }}
              role="group"
              aria-label="Time-of-day playback controls"
            >
              <button
                type="button"
                aria-label={playing ? 'Pause time-of-day animation' : 'Play time-of-day animation'}
                aria-pressed={playing}
                onClick={() => setPlaying((p) => !p)}
                style={playbackButtonStyle()}
              >
                <Icon
                  name={playing ? 'pause' : 'play_arrow'}
                  size={20}
                  decorative
                />
              </button>
              <button
                type="button"
                aria-label="Stop time-of-day animation"
                onClick={() => {
                  setPlaying(false);
                  onChange('currentHour', 13.0);
                }}
                style={playbackButtonStyle()}
              >
                <Icon name="stop" size={20} decorative />
              </button>
            </div>
          ) : null}

          <ParamSwitch
            label="Loop time-of-day playback"
            checked={params.loop}
            onChange={(v) => onChange('loop', v)}
            hint="Time wraps 23:59 → 00:00 continuously"
          />
        </>
      ) : (
        <p
          style={{
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          {params.mode === 'project-clock'
            ? 'Time driven by the 4D project timeline.'
            : 'Time driven by the browser system clock.'}
        </p>
      )}

      {reducedMotion && params.mode === 'manual' && params.playbackSpeed > 1 ? (
        <p
          style={{
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.muted,
          }}
        >
          Playback updates are throttled to 1 Hz (Reduce Motion is on).
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function EnvironmentToolCard({
  layerId,
  environmentKind,
  parameters,
  onParameterChange,
  onClose,
}: EnvironmentToolCardProps) {
  const reducedMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelBodyRef = useRef<HTMLDivElement | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [errorAnnouncement, _setErrorAnnouncement] = useState('');

  const label = ENVIRONMENT_KIND_LABEL[environmentKind];
  const icon = ENVIRONMENT_KIND_ICON[environmentKind];

  const announceDebounced = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (text: string) => {
      clearTimeout(timer);
      timer = setTimeout(() => setAnnouncement(text), 500);
    };
  }, []);

  // Focus the first parameter control when mounted (skip close button).
  useEffect(() => {
    const timer = setTimeout(() => {
      const body = panelBodyRef.current;
      if (body) {
        const firstParam = body.querySelector<HTMLElement>(
          'input, button, [role="radio"], [role="switch"], [role="slider"]',
        );
        firstParam?.focus();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // Escape closes the panel (a11y brief §2.2, §6.5).
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  const handleChange = useCallback(
    (key: string, value: unknown) => {
      onParameterChange(key, value);

      if (typeof value === 'boolean') {
        setAnnouncement(`${key} ${value ? 'enabled' : 'disabled'}`);
      } else if (typeof value === 'number') {
        announceDebounced(`${key} changed to ${value}`);
      }
    },
    [onParameterChange, announceDebounced],
  );

  const p = parameters as Record<string, unknown>;

  return (
    <div
      ref={panelRef}
      role="region"
      aria-label={`${label} parameters panel`}
      onKeyDown={handleKeyDown}
      data-layer-id={layerId}
      style={panelContainerStyle()}
    >
      {/* Panel header */}
      <div style={panelHeaderStyle()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
          <Icon
            name={icon}
            size={20}
            decorative
            style={{ color: tokens.color.accent.primary }}
          />
          <h3
            style={{
              margin: 0,
              fontSize: tokens.font.role.body.size,
              fontWeight: tokens.font.weight.semibold,
              color: tokens.color.ink.primary,
            }}
          >
            {label}
          </h3>
          <span
            title={`Weather module v${WEATHER_MODULE_VERSION}`}
            style={{
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
            }}
          >
            v{WEATHER_MODULE_VERSION}
          </span>
        </div>
        <button
          type="button"
          aria-label={`Close ${label} parameters`}
          onClick={onClose}
          style={closeButtonStyle()}
        >
          <Icon name="close" size={20} decorative />
        </button>
      </div>

      {/* Parameter panel body */}
      <div ref={panelBodyRef} style={panelBodyStyle()}>
        {renderPanel(environmentKind, p, handleChange, reducedMotion)}
      </div>

      {/* Screen reader announcement regions */}
      <div
        aria-live="polite"
        role="status"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
        }}
      >
        {announcement}
      </div>
      <div
        aria-live="assertive"
        role="alert"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
        }}
      >
        {errorAnnouncement}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel dispatcher
// ---------------------------------------------------------------------------

function renderPanel(
  kind: EnvironmentKind,
  params: Record<string, unknown>,
  onChange: (key: string, value: unknown) => void,
  reducedMotion: boolean,
): React.ReactNode {
  switch (kind) {
    case 'sky':
      return (
        <SkyPanel
          params={params as unknown as SkyParameters}
          onChange={onChange}
          reducedMotion={reducedMotion}
        />
      );
    case 'sun':
      return (
        <SunPanel
          params={params as unknown as SunParameters}
          onChange={onChange}
          reducedMotion={reducedMotion}
        />
      );
    case 'moon':
      return (
        <MoonPanel
          params={params as unknown as MoonParameters}
          onChange={onChange}
        />
      );
    case 'stars':
      return (
        <StarsPanel
          params={params as unknown as StarsParameters}
          onChange={onChange}
          reducedMotion={reducedMotion}
        />
      );
    case 'fog':
      return (
        <FogPanel
          params={params as unknown as FogParameters}
          onChange={onChange}
        />
      );
    case 'clouds':
      return (
        <CloudsPanel
          params={params as unknown as CloudsParameters}
          onChange={onChange}
        />
      );
    case 'wind':
      return (
        <WindPanel
          params={params as unknown as WindParameters}
          onChange={onChange}
        />
      );
    case 'rain':
      return (
        <RainPanel
          params={params as unknown as RainParameters}
          onChange={onChange}
        />
      );
    case 'snow':
      return (
        <SnowPanel
          params={params as unknown as SnowParameters}
          onChange={onChange}
        />
      );
    case 'lightning':
      return <LightningPanel />;
    case 'darkmode':
      return (
        <DarkmodePanel
          params={params as unknown as DarkmodeParameters}
          onChange={onChange}
        />
      );
    case 'time-of-day':
      return (
        <TimeOfDayPanel
          params={params as unknown as TimeOfDayParameters}
          onChange={onChange}
          reducedMotion={reducedMotion}
        />
      );
  }
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

function panelContainerStyle(): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    background: tokens.color.surface[2],
    borderLeft: `2px solid ${tokens.color.accent.primary}`,
    height: '100%',
    overflow: 'hidden',
  };
}

function panelHeaderStyle(): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `0 ${tokens.space[3]}px`,
    minHeight: tokens.density.touchMin,
    borderBottom: tokens.border.thin,
    flexShrink: 0,
  };
}

function panelBodyStyle(): CSSProperties {
  return {
    flex: 1,
    overflowY: 'auto',
    padding: tokens.space[3],
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.space[3],
  };
}

function closeButtonStyle(): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: tokens.density.touchMin,
    minHeight: tokens.density.touchMin,
    border: 0,
    borderRadius: tokens.radius.input,
    background: 'transparent',
    color: tokens.color.ink.muted,
    cursor: 'pointer',
    padding: 0,
  };
}

function colorSwatchButtonStyle(color: string): CSSProperties {
  void color;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[2],
    minHeight: tokens.density.touchMin,
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[3],
    cursor: 'pointer',
  };
}

function playbackButtonStyle(): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: tokens.density.touchMin,
    minHeight: tokens.density.touchMin,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[3],
    color: tokens.color.ink.primary,
    cursor: 'pointer',
    padding: 0,
  };
}
